// ClubRegistrationStatus.tsx - Version mit Zahlungsfilter
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Container, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Button, 
  Paper, 
  Typography, 
  Box,
  Chip,
  Tooltip,
  IconButton,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  FormControlLabel,
  Switch
} from "@mui/material";
import dayjs from "dayjs";
import axiosInstance from "../../utils/axiosConfig";
import { AxiosError } from "axios";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import GroupIcon from '@mui/icons-material/Group';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import PaymentDialog from "./PaymentDialog";
import PaymentStatus from "./PaymentStatus";
import { Payment, PaymentStatus as PaymentStatusType } from "../../types/Payment";
import { RegistrationConfirmation } from "../../types/RegistrationConfirmation";
import { paymentService } from "../../services/paymentService";
import { handleApiError } from "../../utils/apiErrorHandler";
import { useAuth } from "../../context/AuthContext";
import PrintableRegistrations from './PrintableRegistrations';
import WaitlistTab from './WaitlistTab';
import PaymentSyncComponent from './PaymentSyncComponent';

import EmailIcon from '@mui/icons-material/Email';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PaymentReminderDialog from "../../components/PaymentReminderDialog";
import RegistrationReminderEmailDialog from './RegistrationReminderEmailDialog';

// Optimierte Datenstruktur von der neuen Backend-Route
interface OptimizedClubEntry {
  clubId: string;
  clubName: string;
  submissionDate: Date;
  totalFee: number;
  totalPenaltyFee: number;
  totalFeeElite: number;
  totalFeeBasic: number;
  totalPenaltyFeeElite: number;
  totalPenaltyFeeBasic: number;
  totalTShirtFee: number;
  totalCateringFee: number;
  totalOvernightFee: number;
  status: string;
  hidden: boolean;
  hasChanges: boolean;
  lastSubmission: Date | null;
  athleteCountElite: number;
  athleteCountBasis: number;
  waitlistCountElite: number;
  waitlistCountBasis: number;
  confirmation: {
    _id: string;
    status: string;
    emailSentAt: string | null;
    pdfGeneratedAt: Date | null;
    fees?: {
      totalAmount: number;
      totalFeeElite?: number;
      totalFeeBasic?: number;
      totalPenaltyFeeElite?: number;
      totalPenaltyFeeBasic?: number;
      totalTShirtFee?: number;
      totalCateringFee?: number;
      totalOvernightFee?: number;
    };
    payment?: Payment;
  } | null;
}

interface ClubsOverviewResponse {
  competitionName: string;
  clubs: OptimizedClubEntry[];
}

interface RegistrationsByClubProps {
  competitionId: string;
}

// Filter- und Sortierungstypen
type PaymentFilterType = 'all' | 'paid' | 'partial' | 'unpaid' | 'no_confirmation';
type ActualPaymentStatusType = 'paid' | 'partial' | 'unpaid' | 'no_confirmation';
type SortOptionType = 'name_asc' | 'name_desc' | 'payment_status' | 'date_asc' | 'date_desc';

// TabPanel und andere Komponenten bleiben gleich...
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// Loading Skeleton Komponente
const TableLoadingSkeleton: React.FC = () => (
  <TableContainer component={Paper} elevation={2}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Verein</TableCell>
          <TableCell align="center">Status</TableCell>
          <TableCell>Letzte Meldung</TableCell>
          <TableCell align="right">Gebühren</TableCell>
          <TableCell align="center">Aktionen</TableCell>
          <TableCell align="center">Meldebestätigung</TableCell>
          <TableCell align="center">Zahlung</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(3)].map((_, index) => (
          <TableRow key={index}>
            <TableCell><Skeleton width={150} /></TableCell>
            <TableCell align="center"><Skeleton width={80} /></TableCell>
            <TableCell><Skeleton width={120} /></TableCell>
            <TableCell align="right"><Skeleton width={60} /></TableCell>
            <TableCell align="center"><Skeleton width={100} /></TableCell>
            <TableCell align="center"><Skeleton width={100} /></TableCell>
            <TableCell align="center"><Skeleton width={100} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ClubRegistrationStatusPage: React.FC<RegistrationsByClubProps> = ({ competitionId }) => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [clubs, setClubs] = useState<OptimizedClubEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [competitionName, setCompetitionName] = useState<string>("");
  
  // Filter und Sortierung States
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilterType>('all');
  const [sortOption, setSortOption] = useState<SortOptionType>('date_desc');
  
  // Payment Dialog State
  const [paymentDialogOpen, setPaymentDialogOpen] = useState<boolean>(false);
  const [selectedConfirmation, setSelectedConfirmation] = useState<RegistrationConfirmation | null>(null);
  
  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [paymentToDelete, setPaymentToDelete] = useState<Payment | null>(null);

  // Meldung löschen
  const [confirmDeleteClub, setConfirmDeleteClub] = useState<OptimizedClubEntry | null>(null);

  // Tab State
  const [tabValue, setTabValue] = useState(0);
  const isHelper = userRole === 'helper';

  // Ausgeblendete Meldungen anzeigen
  const [showHidden, setShowHidden] = useState<boolean>(false);

  // Payment Reminder Dialog State
  const [paymentReminderDialogOpen, setPaymentReminderDialogOpen] = useState<boolean>(false);
  const [selectedReminderConfirmation, setSelectedReminderConfirmation] = useState<{
    confirmationId: string;
    clubName: string;
    totalAmount: number;
  } | null>(null);

  // Meldungserinnerung Dialog State
  const [reminderDialogOpen, setReminderDialogOpen] = useState<boolean>(false);
  const [selectedReminderClub, setSelectedReminderClub] = useState<{ clubId: string; clubName: string } | null>(null);

  // Sportler hinzufügen Dialog State
  const [addAthleteDialogOpen, setAddAthleteDialogOpen] = useState(false);
  const [addAthleteClub, setAddAthleteClub] = useState<OptimizedClubEntry | null>(null);
  const [addAthleteDataLoading, setAddAthleteDataLoading] = useState(false);
  const [addAthleteLoading, setAddAthleteLoading] = useState(false);
  const [availableAthletes, setAvailableAthletes] = useState<{ _id: string; firstname: string; surname: string; age: number; gender: string }[]>([]);
  const [availableGroups, setAvailableGroups] = useState<{ _id: string; name: string; type: string }[]>([]);
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 🚀 OPTIMIERTER useEffect - nur noch EIN API-Aufruf!
  useEffect(() => {
    const fetchClubsOverview = async () => {
      setLoading(true);
      try {
        // Ein einziger API-Aufruf für alle Daten
        const response = await axiosInstance.get<ClubsOverviewResponse>(
          `/api/competition-registrations/${competitionId}/clubs-overview`
        );
        
        const { competitionName, clubs } = response.data;
        
        setCompetitionName(competitionName);
        setClubs(clubs);
        
      } catch (error) {
        handleApiError(error, "Fehler beim Laden der Vereinsmeldungen.");
      } finally {
        setLoading(false);
      }
    };

    fetchClubsOverview();
  }, [competitionId]);

  // Hilfsfunktion: Zahlungsstatus eines Clubs ermitteln
  const getPaymentStatus = (club: OptimizedClubEntry): ActualPaymentStatusType => {
    if (!club.confirmation) return 'no_confirmation';
    if (!club.confirmation.payment) return 'unpaid';
    
    // Aktuellen erwarteten Betrag aus der Meldung nehmen
    const currentExpectedAmount = Number(getTotalFeesNumeric(club).toFixed(2));
    const paidAmount = Number((club.confirmation.payment.amount || 0).toFixed(2));
    
    // Status basierend auf aktuellem Betrag berechnen
    if (paidAmount === currentExpectedAmount) {
      return 'paid';
    } else if (paidAmount > 0) {
      return 'partial';
    } else {
      return 'unpaid';
    }
    
    //return club.confirmation.payment.status as ActualPaymentStatusType;
  };

  // Hilfsfunktion für numerischen Gesamtbetrag
  const getTotalFeesNumeric = (club: OptimizedClubEntry): number => {
    if (club.confirmation?.fees != null && club.confirmation.fees.totalAmount != null) {
      return club.confirmation.fees.totalAmount;
    }
    
    // 🔹 PRIORITÄT 2: Sonst verwende berechnete Gebühren aus aktueller Meldung
    const calculatedTotal = (
      (club.totalFee ?? 0) +
      (club.totalPenaltyFee ?? 0) +
      (club.totalTShirtFee ?? 0) +
      (club.totalCateringFee ?? 0) +
      (club.totalOvernightFee ?? 0)
    );

    return calculatedTotal;
  };

  // 🆕 Gefilterte und sortierte Clubs
  const filteredAndSortedClubs = useMemo(() => {
    let filtered = clubs;

    // Ausgeblendete Meldungen herausfiltern (außer Toggle aktiv)
    if (!showHidden) {
      filtered = filtered.filter(club => !club.hidden);
    }

    // Filter anwenden
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(club => {
        const status = getPaymentStatus(club);
        return status === paymentFilter;
      });
    }
    
    // Sortierung anwenden
    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return a.clubName.localeCompare(b.clubName);
        case 'name_desc':
          return b.clubName.localeCompare(a.clubName);
        case 'payment_status':
          const statusOrder: Record<ActualPaymentStatusType, number> = { 
            'paid': 0, 
            'partial': 1, 
            'unpaid': 2, 
            'no_confirmation': 3 
          };
          const statusA = getPaymentStatus(a);
          const statusB = getPaymentStatus(b);
          const orderA = statusOrder[statusA];
          const orderB = statusOrder[statusB];
          return orderA - orderB;
        case 'date_asc':
          const dateA = a.submissionDate ? new Date(a.submissionDate).getTime() : 0;
          const dateB = b.submissionDate ? new Date(b.submissionDate).getTime() : 0;
          return dateA - dateB;
        case 'date_desc':
          const dateA2 = a.submissionDate ? new Date(a.submissionDate).getTime() : 0;
          const dateB2 = b.submissionDate ? new Date(b.submissionDate).getTime() : 0;
          return dateB2 - dateA2;
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [clubs, paymentFilter, sortOption, showHidden]);

  // 🆕 Statistiken für Filter-Badges
  const paymentStats = useMemo(() => {
    const stats = {
      all: clubs.length,
      paid: 0,
      partial: 0,
      unpaid: 0,
      no_confirmation: 0
    };
    
    clubs.forEach(club => {
      const status = getPaymentStatus(club);
      // Type-safe increment
      if (status in stats) {
        stats[status as keyof typeof stats]++;
      }
    });
    
    return stats;
  }, [clubs]);

  // Hilfsfunktionen bleiben gleich
  const getTotalFees = (club: OptimizedClubEntry) => {
    return getTotalFeesNumeric(club).toFixed(2);
  };

  const getFeeBreakdown = (club: OptimizedClubEntry) => {
    const useConfirmationFees = club.confirmation?.fees;
    const gutschriftAmount: number = (club.confirmation?.fees as any)?.gutschrift?.amount || 0;
    const gutschriftDescription: string = (club.confirmation?.fees as any)?.gutschrift?.description || '';

    const fees = useConfirmationFees ? {
      totalFeeElite: club.confirmation!.fees!.totalFeeElite ?? 0,
      totalFeeBasic: club.confirmation!.fees!.totalFeeBasic ?? 0,
      totalPenaltyFeeElite: club.confirmation!.fees!.totalPenaltyFeeElite ?? 0,
      totalPenaltyFeeBasic: club.confirmation!.fees!.totalPenaltyFeeBasic ?? 0,
      totalTShirtFee: club.confirmation!.fees!.totalTShirtFee ?? 0,
      totalCateringFee: club.confirmation!.fees!.totalCateringFee ?? 0,
      totalOvernightFee: club.confirmation!.fees!.totalOvernightFee ?? 0,
      totalAmount: club.confirmation!.fees!.totalAmount ?? 0,
      source: 'Meldebestätigung'
    } : {
      totalFeeElite: club.totalFeeElite ?? 0,
      totalFeeBasic: club.totalFeeBasic ?? 0,
      totalPenaltyFeeElite: club.totalPenaltyFeeElite ?? 0,
      totalPenaltyFeeBasic: club.totalPenaltyFeeBasic ?? 0,
      totalTShirtFee: club.totalTShirtFee ?? 0,
      totalCateringFee: club.totalCateringFee ?? 0,
      totalOvernightFee: club.totalOvernightFee ?? 0,
      totalAmount: (club.totalFee ?? 0) + (club.totalPenaltyFee ?? 0) + (club.totalTShirtFee ?? 0) + (club.totalCateringFee ?? 0) + (club.totalOvernightFee ?? 0),
      source: 'Aktuelle Meldung'
    };

    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1, display: 'block' }}>
          Quelle: {fees.source}
        </Typography>

        <Typography variant="subtitle2">Meldegebühren</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Elite:</Typography>
          <Typography variant="body2">{fees.totalFeeElite.toFixed(2)} €</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Basis:</Typography>
          <Typography variant="body2">{fees.totalFeeBasic.toFixed(2)} €</Typography>
        </Box>

        <Typography variant="subtitle2">Strafgebühren</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Elite:</Typography>
          <Typography variant="body2">{fees.totalPenaltyFeeElite.toFixed(2)} €</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Basis:</Typography>
          <Typography variant="body2">{fees.totalPenaltyFeeBasic.toFixed(2)} €</Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">T-Shirt:</Typography>
          <Typography variant="body2">{fees.totalTShirtFee.toFixed(2)} €</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Verpflegung:</Typography>
          <Typography variant="body2">{fees.totalCateringFee.toFixed(2)} €</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: gutschriftAmount > 0 ? 0.5 : 1 }}>
          <Typography variant="body2">Übernachtung:</Typography>
          <Typography variant="body2">{fees.totalOvernightFee.toFixed(2)} €</Typography>
        </Box>

        {gutschriftAmount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="success.main">
              Gutschrift{gutschriftDescription ? ` (${gutschriftDescription})` : ''}:
            </Typography>
            <Typography variant="body2" color="success.main">- {gutschriftAmount.toFixed(2)} €</Typography>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" fontWeight="bold">Gesamt:</Typography>
          <Typography variant="body2" fontWeight="bold">{fees.totalAmount.toFixed(2)} €</Typography>
        </Box>

        {/* Warnung nur wenn Differenz nach Gutschrift-Berücksichtigung > 0 */}
        {useConfirmationFees && (() => {
          const calculatedTotal = (club.totalFee ?? 0) + (club.totalPenaltyFee ?? 0) + (club.totalTShirtFee ?? 0) + (club.totalCateringFee ?? 0) + (club.totalOvernightFee ?? 0);
          const confirmationTotal = club.confirmation!.fees!.totalAmount!;
          const differenceExcludingGutschrift = Math.abs(calculatedTotal - confirmationTotal - gutschriftAmount);

          if (differenceExcludingGutschrift > 0.01) {
            return (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }}>
                  ⚠️ Bestätigung weicht von aktueller Meldung ab!
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Aktuell berechnet: {calculatedTotal.toFixed(2)} €
                </Typography>
              </>
            );
          }
          return null;
        })()}
      </Box>
    );
  };

  const hasNewerSubmission = (club: OptimizedClubEntry) => {
    if (!club.lastSubmission || !club.confirmation?.emailSentAt) {
      return false;
    }
    
    const lastSubmissionDate = new Date(club.lastSubmission);
    const emailSentDate = new Date(club.confirmation.emailSentAt);
    
    return lastSubmissionDate > emailSentDate;
  };

  // Payment Dialog Handler
  const handleOpenPaymentDialog = (confirmation: NonNullable<OptimizedClubEntry['confirmation']>) => {
    // Verwende den korrekten Gesamtbetrag aus der Meldebestätigung
    const totalAmountFromConfirmation = confirmation.fees?.totalAmount || 0;
    
    const registrationConfirmation: RegistrationConfirmation = {
      _id: confirmation._id,
      competition: competitionId,
      club: confirmation._id,
      status: confirmation.status as any,
      fees: {
        totalFeeElite: confirmation.fees?.totalFeeElite || 0,
        totalFeeBasic: confirmation.fees?.totalFeeBasic || 0,
        totalPenaltyFeeElite: confirmation.fees?.totalPenaltyFeeElite || 0,
        totalPenaltyFeeBasic: confirmation.fees?.totalPenaltyFeeBasic || 0,
        totalTShirtFee: confirmation.fees?.totalTShirtFee || 0,
        totalCateringFee: confirmation.fees?.totalCateringFee || 0,
        totalOvernightFee: confirmation.fees?.totalOvernightFee || 0,
        totalAmount: totalAmountFromConfirmation // Verwende Betrag aus Bestätigung!
      }
    };
    
    setSelectedConfirmation(registrationConfirmation);
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedConfirmation(null);
  };

  const handlePaymentUpdated = (payment: Payment) => {
    setClubs(prevClubs => 
      prevClubs.map(club => {
        if (club.confirmation && 
            (club.confirmation._id === payment.registrationConfirmationId || 
             (typeof payment.registrationConfirmationId === 'object' && 
              club.confirmation._id === payment.registrationConfirmationId._id))) {
          return { 
            ...club, 
            confirmation: {
              ...club.confirmation,
              payment
            }
          };
        }
        return club;
      })
    );
  };

  const handleOpenDeleteDialog = (payment: Payment) => {
    setPaymentToDelete(payment);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPaymentToDelete(null);
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;
    
    try {
      await paymentService.deletePayment(paymentToDelete._id);
      
      setClubs(prevClubs => 
        prevClubs.map(club => {
          if (club.confirmation?.payment && club.confirmation.payment._id === paymentToDelete._id) {
            return { 
              ...club, 
              confirmation: {
                ...club.confirmation,
                payment: undefined
              }
            };
          }
          return club;
        })
      );
      
      handleCloseDeleteDialog();
    } catch (error) {
      handleApiError(error, "Fehler beim Löschen der Zahlung.");
    }
  };

  // Hilfsfunktion: Sollte Zahlungserinnerung angezeigt werden?
  const shouldShowPaymentReminder = (club: OptimizedClubEntry): boolean => {
    if (!club.confirmation) return false;
    
    const paymentStatus = getPaymentStatus(club);
    return paymentStatus === 'unpaid' || paymentStatus === 'partial';
  };
  
  const handleOpenPaymentReminderDialog = (confirmation: NonNullable<OptimizedClubEntry['confirmation']>, clubName: string) => {
    const totalAmountFromConfirmation = confirmation.fees?.totalAmount || 0;
    
    setSelectedReminderConfirmation({
      confirmationId: confirmation._id,
      clubName: clubName,
      totalAmount: totalAmountFromConfirmation
    });
    setPaymentReminderDialogOpen(true);
  };

  const handleClosePaymentReminderDialog = () => {
    setPaymentReminderDialogOpen(false);
    setSelectedReminderConfirmation(null);
  };

  const handlePaymentReminderSent = () => {
    // Optional: Refresh data or show success message
    // You could reload the data here if needed
  };

  const isEmptyRegistration = (club: OptimizedClubEntry): boolean =>
    club.athleteCountElite === 0 && club.athleteCountBasis === 0 && club.lastSubmission !== null;

  const handleToggleVisibility = async (club: OptimizedClubEntry, hide: boolean) => {
    try {
      await axiosInstance.patch(
        `/api/competition-registrations/${competitionId}/${club.clubId}/visibility`,
        { hidden: hide }
      );
      setClubs(prev =>
        prev.map(c => c.clubId === club.clubId ? { ...c, hidden: hide } : c)
      );
    } catch (error) {
      handleApiError(error, hide ? 'Fehler beim Ausblenden der Meldung.' : 'Fehler beim Einblenden der Meldung.');
    }
  };

  const handleDeleteClub = async () => {
    if (!confirmDeleteClub) return;
    try {
      await axiosInstance.delete(
        `/api/competition-registrations/${competitionId}/${confirmDeleteClub.clubId}`
      );
      setClubs(prev => prev.filter(c => c.clubId !== confirmDeleteClub.clubId));
      setConfirmDeleteClub(null);
    } catch (error) {
      handleApiError(error, 'Fehler beim Löschen der Meldung.');
    }
  };

  const handleOpenAddAthleteDialog = async (club: OptimizedClubEntry) => {
    setAddAthleteClub(club);
    setSelectedAthleteId('');
    setSelectedGroupId('');
    setAvailableAthletes([]);
    setAvailableGroups([]);
    setAddAthleteDataLoading(true);
    setAddAthleteDialogOpen(true);
    try {
      const response = await axiosInstance.get(
        `/api/admin/competitions/${competitionId}/clubs/${club.clubId}/unregistered-athletes`
      );
      setAvailableAthletes(response.data.athletes);
      setAvailableGroups(response.data.competitionGroups);
    } catch (error) {
      handleApiError(error, 'Fehler beim Laden der Sportler.');
      setAddAthleteDialogOpen(false);
    } finally {
      setAddAthleteDataLoading(false);
    }
  };

  const handleAddAthlete = async () => {
    if (!addAthleteClub || !selectedAthleteId || !selectedGroupId) return;
    setAddAthleteLoading(true);
    try {
      await axiosInstance.post(
        `/api/admin/competitions/${competitionId}/clubs/${addAthleteClub.clubId}/add-athlete`,
        { athleteId: selectedAthleteId, competitionGroupId: selectedGroupId }
      );
      setAddAthleteDialogOpen(false);
      setAddAthleteClub(null);
      const response = await axiosInstance.get<ClubsOverviewResponse>(
        `/api/competition-registrations/${competitionId}/clubs-overview`
      );
      setCompetitionName(response.data.competitionName);
      setClubs(response.data.clubs);
    } catch (error) {
      handleApiError(error, 'Fehler beim Hinzufügen des Sportlers.');
    } finally {
      setAddAthleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2 
      }}>
        <Typography variant="h6">
          {loading ? <Skeleton width={200} /> : 'Vereinsmeldungen'}
        </Typography>
        {!loading && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <PrintableRegistrations 
              clubs={clubs} 
              competitionName={competitionName} 
            />
            {!isHelper && (
              <PaymentSyncComponent 
                competitionId={competitionId}
                onSyncComplete={() => {
                  // Daten neu laden nach Sync
                  window.location.reload();
                }}
              />
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="club registration tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<GroupIcon />} 
            label="Meldungen" 
            {...a11yProps(0)} 
            iconPosition="start"
          />
          <Tab 
            icon={<HourglassTopIcon />} 
            label="Warteliste" 
            {...a11yProps(1)} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* 🆕 Filter und Sortierung UI */}
        {!loading && (
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
              {/* Zahlungsfilter */}
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <FilterListIcon sx={{ mr: 1, fontSize: 18 }} />
                  Filter nach Zahlungsstatus
                </Typography>
                <ToggleButtonGroup
                  value={paymentFilter}
                  exclusive
                  onChange={(_, newFilter) => newFilter && setPaymentFilter(newFilter)}
                  size="small"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="all" sx={{ gap: 1 }}>
                    Alle
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'text.secondary',
                        fontSize: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 'auto'
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', ml: -0.5 }}>
                      {paymentStats.all}
                    </Typography>
                  </ToggleButton>
                  <ToggleButton value="paid" sx={{ gap: 1 }}>
                    Bezahlt
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'text.secondary',
                        fontSize: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 'auto'
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', ml: -0.5 }} color="success">
                      {paymentStats.paid}
                    </Typography>
                  </ToggleButton>
                  <ToggleButton value="partial" sx={{ gap: 1 }}>
                    Teilweise
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'text.secondary',
                        fontSize: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 'auto'
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', ml: -0.5 }} color="warning">
                      {paymentStats.partial}
                    </Typography>
                  </ToggleButton>
                  <ToggleButton value="unpaid" sx={{ gap: 1 }}>
                    Offen
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'text.secondary',
                        fontSize: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 'auto'
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', ml: -0.5 }} color="error">
                      {paymentStats.unpaid}
                    </Typography>
                  </ToggleButton>
                  <ToggleButton value="no_confirmation" sx={{ gap: 1 }}>
                    Keine Best.
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'text.secondary',
                        fontSize: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 'auto'
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', ml: -0.5 }} color="default">
                      {paymentStats.no_confirmation}
                    </Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Sortierung */}
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <SortIcon sx={{ mr: 1, fontSize: 18 }} />
                  Sortierung
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value as SortOptionType)}
                    displayEmpty
                  >
                    <MenuItem value="name_asc">Vereinsname (A-Z)</MenuItem>
                    <MenuItem value="name_desc">Vereinsname (Z-A)</MenuItem>
                    <MenuItem value="payment_status">Zahlungsstatus</MenuItem>
                    <MenuItem value="date_asc">Datum (älteste zuerst)</MenuItem>
                    <MenuItem value="date_desc">Datum (neueste zuerst)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Leere Meldungen anzeigen */}
              {!isHelper && (
                <Box sx={{ mt: { xs: 1, md: 3 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showHidden}
                        onChange={(e) => setShowHidden(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Leere anzeigen</Typography>}
                  />
                </Box>
              )}

              {/* Ergebnis-Info */}
              <Box sx={{ mt: { xs: 1, md: 3 } }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredAndSortedClubs.length} von {clubs.filter(c => showHidden || !c.hidden).length} Vereinen
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {loading ? (
          <TableLoadingSkeleton />
        ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Verein</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">#TN</TableCell>
                  <TableCell align="center">Warteliste</TableCell>
                  <TableCell>Letzte Meldung</TableCell>
                  <TableCell align="right">Gebühren</TableCell>
                  <TableCell align="center">Aktionen</TableCell>
                  <TableCell align="center">Meldebestätigung</TableCell>
                  <TableCell align="center">Zahlung</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedClubs.map((club: OptimizedClubEntry) => (
                  <TableRow key={club.clubId} sx={club.hidden ? { opacity: 0.6 } : {}}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {club.clubName}
                        {club.hidden && (
                          <Chip label="ausgeblendet" size="small" variant="outlined" color="default" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {club.hasChanges ? 
                        <Chip 
                          icon={<WarningIcon />} 
                          label="Geändert" 
                          color="warning" 
                          size="small" 
                        /> : 
                        club.status === "offen" ?
                          <Chip 
                            icon={<ReportIcon />} 
                            label="Offen" 
                            color="info" 
                            size="small" 
                          />
                          :
                          <Chip 
                          icon={<CheckCircleIcon />} 
                          label="Aktuell" 
                          color="success" 
                          size="small" 
                        />
                      }
                    </TableCell>
                    <TableCell align="center">
                      {(club.athleteCountElite > 0 || club.athleteCountBasis > 0) ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {club.athleteCountElite > 0 && (
                            <Chip label={`E: ${club.athleteCountElite}`} size="small" color="primary" />
                          )}
                          {club.athleteCountBasis > 0 && (
                            <Chip label={`B: ${club.athleteCountBasis}`} size="small" color="secondary" />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">–</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {(club.waitlistCountElite > 0 || club.waitlistCountBasis > 0) ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {club.waitlistCountElite > 0 && (
                            <Chip label={`E: ${club.waitlistCountElite}`} size="small" color="primary" variant="outlined" />
                          )}
                          {club.waitlistCountBasis > 0 && (
                            <Chip label={`B: ${club.waitlistCountBasis}`} size="small" color="secondary" variant="outlined" />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">–</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {club.submissionDate
                        ? dayjs(club.submissionDate).format("DD.MM.YYYY HH:mm")
                        : "Noch nicht versendet"
                      }
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={getFeeBreakdown(club)}
                        arrow
                        placement="left"
                      >
                        <Box>
                          <Typography>{getTotalFees(club)} €</Typography>
                          {((club.confirmation?.fees as any)?.gutschrift?.amount > 0) && (
                            <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                              inkl. Gutschrift
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    
                    {/* 🔹 Rest der Tabellenzellen bleiben weitgehend gleich */}
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Aktuelle Meldung mit Änderungen">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              disabled={!club.hasChanges}
                              onClick={() => navigate(`/orga/competition/${competitionId}/club/${club.clubId}/current-summary`)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Gespeicherte Meldung">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              disabled={!club.lastSubmission}
                              onClick={() => navigate(`/orga/competition/${competitionId}/club/${club.clubId}/saved-summary`)}
                            >
                              <DescriptionIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!isHelper && !club.hidden && (isEmptyRegistration(club) || !club.lastSubmission) && (
                          <Tooltip title="Meldung ausblenden">
                            <IconButton
                              size="small"
                              onClick={() => handleToggleVisibility(club, true)}
                            >
                              <VisibilityOffIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isHelper && club.hidden && (
                          <Tooltip title="Meldung wieder einblenden">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleToggleVisibility(club, false)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isHelper && (
                          <Tooltip title="Meldung komplett löschen">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDeleteClub(club)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isHelper && (
                          <Tooltip title="Sportler zur Meldung hinzufügen">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenAddAthleteDialog(club)}
                            >
                              <PersonAddIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isHelper && (
                          <Tooltip title="Meldungserinnerung per E-Mail senden">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => {
                                setSelectedReminderClub({ clubId: club.clubId, clubName: club.clubName });
                                setReminderDialogOpen(true);
                              }}
                            >
                              <ForwardToInboxIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    
                    {/* Meldebestätigung und Zahlung Spalten bleiben gleich ... */}
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="PDF ansehen">
                          <span>
                            <IconButton 
                              color="primary" 
                              size="small"
                              disabled={!club.confirmation?._id}
                              onClick={() => navigate(`/orga/competition/${competitionId}/confirmation-registration/${club.confirmation?._id}`)}                          
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        
                        {!isHelper && (
                          <Tooltip title="Meldebestätigung erstellen">
                            <span>
                              <IconButton 
                                color="primary" 
                                size="small"
                                disabled={!club.lastSubmission}
                                onClick={() => navigate(`/orga/competition/${competitionId}/confirmation/${club.clubId}`)}
                              >
                                <AddCircleOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                      {club.confirmation?.emailSentAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
                          <Typography variant="caption">
                            Versendet: {dayjs(club.confirmation.emailSentAt).format("DD.MM.YYYY")}
                          </Typography>
                          
                          {hasNewerSubmission(club) && (
                            <Tooltip title="Es gibt eine neuere Meldung nach dieser Bestätigung!">
                              <WarningAmberIcon 
                                color="warning" 
                                fontSize="small" 
                                sx={{ ml: 0.5, fontSize: 16 }} 
                              />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    
                    <TableCell align="center">
                      {club.confirmation?._id ? (
                        <Box>
                          {club.confirmation.payment ? (
                            <Box sx={{ mb: 1 }}>
                              <PaymentStatus 
                                status={club.confirmation.payment.status} 
                                amount={club.confirmation.payment.amount}
                                expectedAmount={club.confirmation.payment.expectedAmount}
                                currentExpectedAmount={getTotalFeesNumeric(club)}
                                showInconsistency={true}
                                compact
                              />
                            </Box>
                          ) : (
                            <Box sx={{ mb: 1 }}>
                              <Chip 
                                label="Keine Zahlung" 
                                color="error" 
                                size="small" 
                              />
                            </Box>
                          )}
                          
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title={club.confirmation.payment ? "Zahlung bearbeiten" : "Zahlung hinzufügen"}>
                              <IconButton 
                                color="primary" 
                                size="small"
                                onClick={() => handleOpenPaymentDialog(club.confirmation!)}
                              >
                                <PaymentIcon />
                              </IconButton>
                            </Tooltip>

                            {shouldShowPaymentReminder(club) && (
                              <Tooltip title="Zahlungserinnerung senden">
                                <IconButton 
                                  color="warning" 
                                  size="small"
                                  onClick={() => handleOpenPaymentReminderDialog(club.confirmation!, club.clubName)}
                                >
                                  <EmailIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {club.confirmation.payment && (
                              <Tooltip title="Zahlung löschen">
                                <IconButton 
                                  color="error" 
                                  size="small"
                                  onClick={() => handleOpenDeleteDialog(club.confirmation!.payment!)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                          
                          {club.confirmation.payment && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              Eingegangen: {dayjs(club.confirmation.payment.receivedDate).format("DD.MM.YYYY")}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Zunächst Bestätigung erstellen
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Keine Ergebnisse Nachricht */}
                {filteredAndSortedClubs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Keine Vereine entsprechen dem gewählten Filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <WaitlistTab competitionId={competitionId} />
      </TabPanel>

      {/* Dialoge bleiben gleich */}
      {selectedConfirmation && (
        <PaymentDialog
          open={paymentDialogOpen}
          onClose={handleClosePaymentDialog}
          registrationConfirmation={selectedConfirmation}
          onPaymentUpdated={handlePaymentUpdated}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Zahlung löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest Du diese Zahlung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Abbrechen
          </Button>
          <Button onClick={handleDeletePayment} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {selectedReminderConfirmation && (
        <PaymentReminderDialog
          open={paymentReminderDialogOpen}
          onClose={handleClosePaymentReminderDialog}
          confirmationId={selectedReminderConfirmation.confirmationId}
          clubName={selectedReminderConfirmation.clubName}
          totalAmount={selectedReminderConfirmation.totalAmount}
          onReminderSent={handlePaymentReminderSent}
        />
      )}

      {/* Bestehende Dialoge bleiben unverändert */}
      {selectedConfirmation && (
        <PaymentDialog
          open={paymentDialogOpen}
          onClose={handleClosePaymentDialog}
          registrationConfirmation={selectedConfirmation}
          onPaymentUpdated={handlePaymentUpdated}
        />
      )}

      <Dialog open={!!confirmDeleteClub} onClose={() => setConfirmDeleteClub(null)}>
        <DialogTitle>Meldung komplett löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll die gesamte Meldung von <strong>{confirmDeleteClub?.clubName}</strong> wirklich gelöscht werden?
            Dabei werden alle Athleten-, Kampfrichter- und Offiziellen-Registrierungen, Meldebestätigungen sowie Zahlungen
            unwiderruflich entfernt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteClub(null)} color="inherit">
            Abbrechen
          </Button>
          <Button onClick={handleDeleteClub} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {selectedReminderClub && (
        <RegistrationReminderEmailDialog
          open={reminderDialogOpen}
          onClose={() => { setReminderDialogOpen(false); setSelectedReminderClub(null); }}
          competitionId={competitionId}
          clubId={selectedReminderClub.clubId}
          clubName={selectedReminderClub.clubName}
          competitionName={competitionName}
          onSent={() => {}}
        />
      )}

      <Dialog open={addAthleteDialogOpen} onClose={() => setAddAthleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Sportler zur Meldung hinzufügen
          {addAthleteClub && (
            <Typography variant="body2" color="text.secondary">{addAthleteClub.clubName}</Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {addAthleteDataLoading ? (
            <Box sx={{ pt: 2 }}>
              <Skeleton height={56} sx={{ mb: 2 }} />
              <Skeleton height={56} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {availableAthletes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Keine nicht gemeldeten Sportler für diesen Verein vorhanden.
                </Typography>
              ) : (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Sportler</InputLabel>
                    <Select
                      value={selectedAthleteId}
                      label="Sportler"
                      onChange={e => setSelectedAthleteId(e.target.value)}
                    >
                      {availableAthletes.map(a => (
                        <MenuItem key={a._id} value={a._id}>
                          {a.firstname} {a.surname} ({a.age}, {a.gender === 'm' ? 'männlich' : 'weiblich'})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Wettkampfgruppe</InputLabel>
                    <Select
                      value={selectedGroupId}
                      label="Wettkampfgruppe"
                      onChange={e => setSelectedGroupId(e.target.value)}
                    >
                      {availableGroups.map(g => (
                        <MenuItem key={g._id} value={g._id}>
                          {g.name} ({g.type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddAthleteDialogOpen(false)} color="inherit" disabled={addAthleteLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleAddAthlete}
            variant="contained"
            color="success"
            disabled={!selectedAthleteId || !selectedGroupId || addAthleteLoading || addAthleteDataLoading}
          >
            {addAthleteLoading ? 'Wird hinzugefügt...' : 'Hinzufügen und Meldung abschließen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClubRegistrationStatusPage;